import type { Resource } from "@/types";

/**
 * Fetches Resources from the backend API.
 *
 * @returns A promise that resolves to a Resource[].
 * @throws An error if the fetch operation fails.
 */
export const fetchResources = async (): Promise<Resource[]> => {
	const url = "/api/v1/resources";
	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorBody = await response.text();
			throw new Error(
				`API request failed with status ${response.status}: ${errorBody}`,
			);
		}

		const data = await response.json();

		if (data) {
			return data as Resource[];
		}

		throw new Error("Invalid response format from server");
	} catch (error) {
		console.error("Error fetching resources:", error);
		throw new Error("Failed to fetch resources. Please try again later.");
	}
};

/**
 * Creates a new Resource.
 *
 * @param resource The resource to create.
 * @returns A promise that resolves to the created Resource.
 */
export const createResource = async (
	resource: Omit<Resource, "id" | "createdAt">,
): Promise<Resource> => {
	const token = localStorage.getItem("adafia_auth_token");
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};

	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch("/api/v1/resources", {
		method: "POST",
		headers,
		body: JSON.stringify(resource),
		credentials: "include",
	});

	if (!response.ok) {
		const errorBody = await response.text();
		throw new Error(
			`API request failed with status ${response.status}: ${errorBody}`,
		);
	}

	return response.json();
};

/**
 * Updates an existing Resource.
 *
 * @param id The ID of the resource to update.
 * @param resource The resource data to update.
 * @returns A promise that resolves to the updated Resource.
 */
export const updateResource = async (
	id: number,
	resource: Partial<Omit<Resource, "id" | "createdAt">>,
): Promise<Resource> => {
	const token = localStorage.getItem("adafia_auth_token");
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};

	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(`/api/v1/resources/${id}`, {
		method: "PATCH",
		headers,
		body: JSON.stringify(resource),
		credentials: "include",
	});

	if (!response.ok) {
		const errorBody = await response.text();
		throw new Error(
			`API request failed with status ${response.status}: ${errorBody}`,
		);
	}

	return response.json();
};

/**
 * Deletes a Resource.
 *
 * @param id The ID of the resource to delete.
 */
export const deleteResource = async (id: number): Promise<void> => {
	const token = localStorage.getItem("adafia_auth_token");
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};

	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const response = await fetch(`/api/v1/resources/${id}`, {
		method: "DELETE",
		headers,
		credentials: "include",
	});

	if (!response.ok) {
		const errorBody = await response.text();
		throw new Error(
			`API request failed with status ${response.status}: ${errorBody}`,
		);
	}
};
